import { useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { InvalidField, RealNameSuspect, TITLE_MAX } from "../api/teacherApi";
import type { TeacherApi } from "../api/teacherApi";
import type { TeacherWorkItem, TitleCheck, WorkPatch } from "../types";
import { PRINCIPLE_TAGS, sameTags, toggleTag } from "../lib/tags";

export function checkTitle(raw: string): TitleCheck {
  const v = raw.trim();
  if (!v) return "empty";
  return [...v].length > TITLE_MAX ? "long" : "ok";
}

type Status =
  | { k: "idle" }
  | { k: "saving" }
  | { k: "suspect"; patch: WorkPatch }
  | { k: "invalid"; field: string | null }
  | { k: "error"; code: string }
  | { k: "saved" };

/**
 * 작품명 · 교사 태그 편집(FS-11-07 · 카드 ✎ — FS-11-PH1 에서도 같은 편집, variant "tile").
 *  - 작품명 title_override ≤20자 · '실명 대신 별명·작품 이름만' 안내 · '자동 이름으로 되돌리기'(titleOverride null)
 *  - 태그 팔레트(PRINCIPLE_TAGS 안에서만) — 저장 시 teacher_tags · '자동 태그로 되돌리기'(teacherTags null)
 *  - 실명 의심(409): 경고 배너 → 교사가 '확인하고 저장'을 눌러야 confirmSuspect 로 저장(SR-SEC-01)
 * 스냅샷 본문·해시는 그대로(메타) — 학생 화면은 표시만. 부르는 쪽이 key={snapshotId} 로 작품마다 새로 그린다.
 */
export function WorkEditor({
  work,
  api,
  variant = "full",
  onSaved,
  onClose,
}: {
  work: TeacherWorkItem;
  api: TeacherApi;
  variant?: "full" | "tile";
  onSaved: (w: TeacherWorkItem) => void;
  onClose?: () => void;
}) {
  const t = useT();
  const id = useId();
  const [title, setTitle] = useState(work.title);
  const [tags, setTags] = useState<string[]>(work.tags);
  const [status, setStatus] = useState<Status>({ k: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (variant === "tile") inputRef.current?.focus();
  }, [variant]);

  const check = checkTitle(title);
  const titleChanged = title.trim() !== work.title;
  const tagsChanged = variant === "full" && !sameTags(tags, work.tags);
  const busy = status.k === "saving";
  const len = [...title.trim()].length;

  async function send(patch: WorkPatch) {
    setStatus({ k: "saving" });
    try {
      const w = await api.patchWork(work.snapshotId, patch);
      onSaved(w);
      setTitle(w.title);
      setTags(w.tags);
      setStatus({ k: "saved" });
    } catch (e) {
      if (e instanceof RealNameSuspect) setStatus({ k: "suspect", patch });
      else if (e instanceof InvalidField) setStatus({ k: "invalid", field: e.field });
      else setStatus({ k: "error", code: e instanceof ApiError ? e.code : "NET-SERVER" });
    }
  }

  function save() {
    if (check !== "ok" && titleChanged) {
      inputRef.current?.focus();
      return;
    }
    const patch: WorkPatch = {};
    if (titleChanged) patch.titleOverride = title.trim();
    if (tagsChanged) patch.teacherTags = tags;
    if (!("titleOverride" in patch) && !("teacherTags" in patch)) {
      onClose?.();
      return;
    }
    void send(patch);
  }

  const titleErr =
    titleChanged && check !== "ok"
      ? t(check === "empty" ? "teacher.works.titleEmpty" : "teacher.works.titleLong", {
          max: TITLE_MAX,
        })
      : status.k === "invalid" && status.field === "titleOverride"
        ? t("teacher.works.titleInvalid", { max: TITLE_MAX })
        : null;

  const input = (
    <input
      ref={inputRef}
      id={`${id}-title`}
      className="pf-input"
      type="text"
      value={title}
      maxLength={TITLE_MAX + 10}
      autoComplete="off"
      spellCheck={false}
      aria-invalid={titleErr ? true : undefined}
      aria-describedby={`${id}-count ${id}-note${titleErr ? ` ${id}-err` : ""}`}
      data-testid="title-input"
      onChange={(e) => {
        setTitle(e.target.value);
        if (status.k !== "saving") setStatus({ k: "idle" });
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        if (e.key === "Escape") onClose?.();
      }}
    />
  );
  const counter = (
    <span
      id={`${id}-count`}
      className="pf-counter"
      data-testid="title-count"
      aria-label={t("teacher.works.countLabel", { n: len, max: TITLE_MAX })}
    >
      {t("teacher.works.count", { n: len, max: TITLE_MAX })}
    </span>
  );
  const errorLine = titleErr && (
    <span id={`${id}-err`} className="pf-field-err" role="alert">
      {titleErr}
    </span>
  );
  const suspect = status.k === "suspect" && (
    <div className="pf-banner pf-banner--warn" role="alert" data-testid="title-suspect">
      <div className="pf-banner__body">
        <p className="pf-banner__text">
          {t("teacher.works.suspect", { title: status.patch.titleOverride ?? "" })}
        </p>
        <div className="pf-banner__actions">
          <Button
            variant="ghost"
            className="pf-btn--sm"
            onClick={() => {
              setStatus({ k: "idle" });
              inputRef.current?.focus();
            }}
          >
            {t("teacher.nick.rewrite")}
          </Button>
          <Button
            variant="accent"
            className="pf-btn--sm"
            data-testid="title-confirm"
            onClick={() => void send({ ...status.patch, confirmSuspect: true })}
          >
            {t("teacher.nick.confirmSave")}
          </Button>
        </div>
      </div>
    </div>
  );
  const failure = status.k === "error" && (
    <ErrorBanner
      cause={t("teacher.works.saveFailed")}
      action={t("teacher.common.retryLater")}
      code={status.code}
    />
  );

  if (variant === "tile")
    return (
      <div className="pf-tile-editor" data-testid="tile-editor">
        <label htmlFor={`${id}-title`} className="pf-sr-only">
          {t("teacher.works.titleLabel")}
        </label>
        <div className="pf-row">
          {input}
          {counter}
        </div>
        {errorLine}
        <div className="pf-row">
          <Button
            variant="accent"
            className="pf-btn--sm"
            disabled={busy}
            data-testid="title-save"
            onClick={save}
          >
            {t("teacher.common.save")}
          </Button>
          <Button
            variant="ghost"
            className="pf-btn--sm"
            disabled={busy || work.titleOverride === null}
            data-testid="title-revert"
            onClick={() => void send({ titleOverride: null })}
          >
            {t("teacher.works.autoShort")}
          </Button>
        </div>
        <span id={`${id}-note`} className="pf-note">
          {t("teacher.works.realNameShort")}
        </span>
        {suspect}
        {failure}
      </div>
    );

  return (
    <section
      className="pf-editor-pane"
      aria-labelledby={`${id}-head`}
      data-testid="work-editor"
      data-work={work.snapshotId}
    >
      <div className="pf-editor-pane__head">
        <h3 id={`${id}-head`} tabIndex={-1}>
          {work.nickname} · {work.autoTitle}
        </h3>
        <span className="pf-muted">{t("teacher.works.editorSub")}</span>
        <Button
          variant="accent"
          className="pf-btn--sm pf-row__end"
          disabled={busy}
          data-testid="work-save"
          onClick={save}
        >
          {status.k === "saved" && !titleChanged && !tagsChanged
            ? t("common.save.saved")
            : t("teacher.common.save")}
        </Button>
      </div>
      <div className="pf-field-row">
        <label htmlFor={`${id}-title`} className="pf-field-label">
          {t("teacher.works.titleLabel")}
        </label>
        {input}
        {counter}
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={busy || work.titleOverride === null}
          data-testid="title-revert"
          onClick={() => void send({ titleOverride: null })}
        >
          {t("teacher.works.revertTitle")}
        </Button>
      </div>
      {errorLine}
      <p id={`${id}-note`} className="pf-note">
        {t("teacher.works.realNameNote")}
      </p>
      {suspect}
      <div className="pf-field-row">
        <span className="pf-field-label" id={`${id}-pal`}>
          {t("teacher.works.palette")}
        </span>
        <div className="pf-palette" role="group" aria-labelledby={`${id}-pal`}>
          {PRINCIPLE_TAGS.map((tag) => (
            <Button
              key={tag}
              variant="ghost"
              className="pf-btn--sm"
              aria-pressed={tags.includes(tag)}
              data-tag={tag}
              onClick={() => setTags((xs) => toggleTag(xs, tag))}
            >
              {tag}
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={busy || work.teacherTags === null}
          data-testid="tags-revert"
          onClick={() => void send({ teacherTags: null })}
        >
          {t("teacher.works.revertTags")}
        </Button>
      </div>
      {status.k === "invalid" && status.field === "teacherTags" && (
        <span className="pf-field-err" role="alert">
          {t("teacher.works.tagsInvalid")}
        </span>
      )}
      <p className="pf-note">{t("teacher.works.tagsNote")}</p>
      {failure}
      <span className="pf-sr-only" role="status">
        {status.k === "saved" ? t("common.save.saved") : busy ? t("common.save.saving") : ""}
      </span>
    </section>
  );
}
